import { useFormContext } from 'react-hook-form';
import { FormQuestion, SelectOption } from '@percent/grants-nomination-form/api/nominationForm/nominationForm.types';
import { DateInput, FormField, MultiSelect, Select, TextArea, TextInput } from '@percent/lemonade';
import get from 'lodash/get';
import { formatKey } from '@percent/grants-nomination-form/utils/formKey';
import { NominationFormQuestionProps } from './NominationFormQuestion.types';
import { memo } from 'react';
import { CountrySelect, OrganisationSelect, RegistrySelect } from '@percent/grants-nomination-form/ui';
import { useOrganisationField } from './hook/useOrganisationField';
import { useConditionalSelect } from './hook/useConditionalSelect';
import { useTranslation } from 'react-i18next';
import { MoneyQuestion } from './MoneyQuestion/MoneyQuestion';
import { CurrencyOnlyQuestion } from './CurrencyOnlyQuestion/CurrencyOnlyQuestion';

const DEFAULT_TEXTAREA_MAX_LENGTH = 5000;

export const NominationFormQuestion = memo(
  ({
    question,
    countryCodeQuestionKey,
    organisationQuestionKey,
    allowedOrganisationsTypes = [],
  }: NominationFormQuestionProps) => {
    const { t } = useTranslation();
    const { setValue, getValues, register, formState } = useFormContext();
    const { setOrganisationValue, setQueryValue, toggleAddOrganisation, addOrganisationEnabled, organisation, query } =
      useOrganisationField();
    const { handleConditionalSelectChange } = useConditionalSelect(question);

    // Money/currency questions render their own FormField wrapper.
    if (question.type === 'money') {
      return <MoneyQuestion question={question} />;
    }
    if (question.type === 'currency') {
      return <CurrencyOnlyQuestion question={question} />;
    }

    // We want to show the error only if the field has been touched
    const shouldShowError = get(formState.touchedFields, formatKey(question.key));
    const error = shouldShowError && get(formState.errors, formatKey(question.key));

    const setFieldValue = (value: string) => {
      setValue(question.key, value, { shouldValidate: true, shouldTouch: true });
    };

    const onCountryChange = (countryOption: SelectOption) => {
      setFieldValue(countryOption.value);

      if (organisationQuestionKey) {
        // If the organisation field is present, we want to clear it when the country changes
        setOrganisationValue(undefined);
        setQueryValue('');
        toggleAddOrganisation(false);
        setValue(organisationQuestionKey, '', { shouldValidate: true, shouldTouch: false });
      }
    };

    const countryCode = getValues(countryCodeQuestionKey ?? '');

    const renderField = (question: FormQuestion) => {
      switch (question.type) {
        case 'select':
          if (question.multiple) {
            const options = question.options ?? [];
            const currentValues: string[] = getValues(question.key) ?? [];

            return (
              <MultiSelect
                name={question.key}
                placeholder={question.placeholder}
                options={options}
                defaultValue={options.filter((o) => currentValues.includes(o.value))}
                onChange={(newValue: SelectOption[]) => {
                  const values = newValue.map((v) => v.value);
                  setValue(question.key, values, { shouldValidate: true, shouldTouch: true });
                  handleConditionalSelectChange(values);
                }}
                searchable
              />
            );
          }

          return (
            <Select
              name={question.key}
              placeholder={question.placeholder}
              options={question.options?.map((o) => ({ value: o.value, label: o.label })) ?? []}
              defaultValue={question.options?.find((o) => o.value === getValues(question.key)) ?? undefined}
              onChange={(newValue: SelectOption) => {
                setFieldValue(newValue.value);
                handleConditionalSelectChange([newValue.value]);
              }}
              searchable
            />
          );
        case 'textarea':
          return (
            <TextArea
              {...register(question.key)}
              defaultValue={getValues(question.key)}
              placeholder={question.placeholder}
              onChange={(e) => setFieldValue(e.target.value)}
              maxLength={'maxLength' in question ? question.maxLength : DEFAULT_TEXTAREA_MAX_LENGTH}
            />
          );
        case 'country_search':
          return (
            <CountrySelect
              name={question.key}
              placeholder={question.placeholder}
              value={getValues(question.key)}
              onChange={onCountryChange}
            />
          );
        case 'organisation_search':
          return (
            <OrganisationSelect
              name={question.key}
              placeholder={question.placeholder}
              onChange={(newValue: SelectOption) => setFieldValue(newValue.value)}
              setOrganisationValue={setOrganisationValue}
              setQueryValue={setQueryValue}
              addOrganisationEnabled={addOrganisationEnabled}
              toggleAddOrganisation={toggleAddOrganisation}
              countryCode={countryCode}
              query={query}
              organisation={organisation}
              allowedOrganisationsTypes={allowedOrganisationsTypes}
            />
          );
        case 'registry_search':
          return (
            <RegistrySelect
              name={question.key}
              placeholder={question.placeholder}
              onChange={(newValue: SelectOption) => setFieldValue(newValue.value)}
              countryCode={countryCode}
              value={getValues(question.key)}
            />
          );
        case 'number':
          return (
            <TextInput
              {...register(question.key)}
              defaultValue={getValues(question.key)}
              placeholder={question.placeholder}
              type="number"
            />
          );
        case 'date': {
          const currentValue = getValues(question.key);
          const parsedValue = currentValue ? new Date(currentValue) : null;

          return (
            <DateInput
              name={question.key}
              value={parsedValue && !isNaN(parsedValue.getTime()) ? parsedValue : null}
              onChange={(date) => setFieldValue(date ? date.toISOString() : '')}
              placeholder={question.placeholder}
              clearable
              utc
            />
          );
        }
        case 'email':
        default:
          return (
            <TextInput
              {...register(question.key)}
              defaultValue={getValues(question.key)}
              placeholder={question.placeholder}
            />
          );
      }
    };

    const renderDescription = () => {
      if (question.type === 'organisation_search' && (organisation || !countryCode)) {
        // Do not show the description if the organisation is selected
        return;
      }

      return question.description;
    };

    return (
      <FormField
        label={question.label}
        status={error ? 'danger' : 'default'}
        necessity={question.required ? 'required' : 'optional'}
        optionalText={t('typography.optional')}
        statusMessage={error ? error.message?.toString() : undefined}
        description={renderDescription()}
        instructions={question.instructions}
        data-testid={question.key}
      >
        {renderField(question)}
      </FormField>
    );
  },
);
